import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "add-a-custom-shipping-carrier"
    }}>{`Add a custom shipping carrier`}</h1>
    <p>{`This topic describes how to add a custom shipping carrier.`}</p>
    <p>{`To add a new shipping carrier to the checkout:`}</p>
    <ol>
      <li parentName="ol"><a parentName="li" {...{
          "href": "#step-1-create-a-new-module"
        }}>{`Create a new module`}</a></li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "#step-2-add-the-module-configuration"
        }}>{`Add the carrier configuration`}</a></li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "#step-3-create-the-carrier-model"
        }}>{`Create the carrier model`}</a></li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "#step-4-enable-the-module"
        }}>{`Enable the module`}</a></li>
    </ol>
    <h2 {...{
      "id": "step-1-create-a-new-module"
    }}>{`Step 1: Create a new module`}</h2>
    <p>{`The example module for use here is `}<inlineCode parentName="p">{`Vendor_CustomShipping`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "source-code-of-appcodevendorcustomshippingregistrationphp"
    }}>{`Source code of `}<inlineCode parentName="h3">{`app/code/Vendor/CustomShipping/registration.php`}</inlineCode></h3>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php

declare(strict_types=1);

use Magento\\Framework\\Component\\ComponentRegistrar;

ComponentRegistrar::register(
    ComponentRegistrar::MODULE,
    'Vendor_CustomShipping',
    __DIR__
);

`}</code></pre>
    <h3 {...{
      "id": "source-code-of-appcodevendorcustomshippingcomposerjson"
    }}>{`Source code of `}<inlineCode parentName="h3">{`app/code/Vendor/CustomShipping/composer.json`}</inlineCode></h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "name": "vendor/module-custom-shipping",
    "description": "Custom shipping module",
    "require": {
        "php": "~7.4.0||~8.1.0",
        "magento/framework": "102.0.*",
        "magento/module-backend": "101.0.*",
        "magento/module-catalog": "103.0.*",
        "magento/module-config": "101.1.*",
        "magento/module-directory": "100.3.*",
        "magento/module-quote": "101.1.*",
        "magento/module-sales": "102.0.*",
        "magento/module-sales-rule": "101.1.*",
        "magento/module-shipping": "100.3.*",
        "magento/module-store": "101.0.*"
    },
    "type": "magento2-module",
    "license": [
        "OSL-3.0",
        "AFL-3.0"
    ],
    "autoload": {
        "files": [
            "registration.php"
        ],
        "psr-4": {
            "Vendor\\\\CustomShipping\\\\": ""
        }
    }
}
`}</code></pre>
    <h3 {...{
      "id": "source-code-of-appcodevendorcustomshippingetcmodulexml"
    }}>{`Source code of `}<inlineCode parentName="h3">{`app/code/Vendor/CustomShipping/etc/module.xml`}</inlineCode></h3>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0"?>
<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:noNamespaceSchemaLocation="urn:magento:framework:Module/etc/module.xsd">
    <module name="Vendor_CustomShipping"/>
</config>
`}</code></pre>
    <h2 {...{
      "id": "step-2-add-the-module-configuration"
    }}>{`Step 2: Add the module configuration`}</h2>
    <p>{`To add a module configuration use the following source code snippets.`}</p>
    <h3 {...{
      "id": "source-code-of-appcodevendorcustomshippingetcadminhtmlsystemxml"
    }}>{`Source code of `}<inlineCode parentName="h3">{`app/code/Vendor/CustomShipping/etc/adminhtml/system.xml`}</inlineCode></h3>
    <p>{`The `}<inlineCode parentName="p">{`system.xml`}</inlineCode>{` source code declares custom shipping module options:`}</p>
    <ul>
      <li parentName="ul">{`Enabled`}</li>
      <li parentName="ul">{`Title`}</li>
      <li parentName="ul">{`Method Name`}</li>
      <li parentName="ul">{`Shipping Cost`}</li>
      <li parentName="ul">{`Ship to Applicable Countries`}</li>
      <li parentName="ul">{`Ship to Specific Countries`}</li>
      <li parentName="ul">{`Show Method if Not Applicable`}</li>
      <li parentName="ul">{`Sort Order`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0"?>
<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:noNamespaceSchemaLocation="urn:magento:module:Magento_Config:etc/system_file.xsd">
    <system>
        <section id="carriers">
            <group id="customshipping" translate="label" type="text" sortOrder="0" showInDefault="1" showInWebsite="1" showInStore="1">
                <label>Custom Shipping Module</label>
                <field id="active" translate="label" type="select" sortOrder="10" showInDefault="1" showInWebsite="1" showInStore="1" canRestore="1">
                    <label>Enabled</label>
                    <source_model>Magento\\Config\\Model\\Config\\Source\\Yesno</source_model>
                </field>
                <field id="title" translate="label" type="text" sortOrder="20" showInDefault="1" showInWebsite="1" showInStore="1" canRestore="1">
                    <label>Title</label>
                </field>
                <field id="name" translate="label" type="text" sortOrder="30" showInDefault="1" showInWebsite="1" showInStore="1" canRestore="1">
                    <label>Method Name</label>
                </field>
                <field id="shipping_cost" translate="label" type="text" sortOrder="40" showInDefault="1" showInWebsite="1" showInStore="1" canRestore="1">
                    <label>Shipping Cost</label>
                    <validate>validate-number validate-zero-or-greater</validate>
                </field>
                <field id="sallowspecific" translate="label" type="select" sortOrder="50" showInDefault="1" showInWebsite="1" showInStore="1" canRestore="1">
                    <label>Ship to Applicable Countries</label>
                    <frontend_class>shipping-applicable-country</frontend_class>
                    <source_model>Magento\\Shipping\\Model\\Config\\Source\\Allspecificcountries</source_model>
                </field>
                <field id="specificcountry" translate="label" type="multiselect" sortOrder="60" showInDefault="1" showInWebsite="1" showInStore="1" canRestore="1">
                    <label>Ship to Specific Countries</label>
                    <source_model>Magento\\Directory\\Model\\Config\\Source\\Country</source_model>
                    <can_be_empty>1</can_be_empty>
                </field>
                <field id="showmethod" translate="label" type="select" sortOrder="70" showInDefault="1" showInWebsite="1" showInStore="1">
                    <label>Show Method if not applicable</label>
                    <source_model>Magento\\Config\\Model\\Config\\Source\\Yesno</source_model>
                    <frontend_class>shipping-skip-hide</frontend_class>
                </field>
                <field id="sort_order" translate="label" type="text" sortOrder="80" showInDefault="1" showInWebsite="1" showInStore="1" canRestore="1">
                    <label>Sort Order</label>
                </field>
            </group>
        </section>
    </system>
</config>
`}</code></pre>
    <h3 {...{
      "id": "source-code-of-appcodevendorcustomshippingetcconfigxml"
    }}>{`Source code of `}<inlineCode parentName="h3">{`app/code/Vendor/CustomShipping/etc/config.xml`}</inlineCode></h3>
    <p>{`The `}<inlineCode parentName="p">{`config.xml`}</inlineCode>{` file specifies default values for custom shipping module options and the shipping module model, `}<inlineCode parentName="p">{`Vendor\\CustomShipping\\Model\\Carrier\\Customshipping`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0"?>
<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:noNamespaceSchemaLocation="urn:magento:module:Magento_Store:etc/config.xsd">
    <default>
        <carriers>
            <customshipping>
                <active>0</active>
                <title>Custom Shipping Title</title>
                <name>Custom Shipping Method Name</name>
                <shipping_cost>10</shipping_cost>
                <sallowspecific>0</sallowspecific>
                <sort_order>15</sort_order>
                <model>Vendor\\CustomShipping\\Model\\Carrier\\Customshipping</model>
            </customshipping>
        </carriers>
    </default>
</config>
`}</code></pre>
    <h2 {...{
      "id": "step-3-create-the-carrier-model"
    }}>{`Step 3: Create the carrier model`}</h2>
    <p>{`In this example, the `}<inlineCode parentName="p">{`Vendor\\CustomShipping\\Model\\Carrier\\Customshipping`}</inlineCode>{` class is a skeleton of a carrier model. You can extend it to fit your needs.`}</p>
    <p>{`The carrier class implements the `}<inlineCode parentName="p">{`CarrierInterface`}</inlineCode>{` interface and retrieves all available shipping methods in the `}<inlineCode parentName="p">{`getAllowedMethods`}</inlineCode>{` function. The `}<inlineCode parentName="p">{`collectRates`}</inlineCode>{` function returns the `}<inlineCode parentName="p">{`\\Magento\\Shipping\\Model\\Rate\\Result`}</inlineCode>{` object if the carrier method is available on checkout. Otherwise, it returns `}<inlineCode parentName="p">{`false`}</inlineCode>{`---the carrier method is not applicable to the shopping cart.`}</p>
    <h3 {...{
      "id": "source-code-of-appcodevendorcustomshippingmodelcarriercustomshippingphp"
    }}>{`Source code of `}<inlineCode parentName="h3">{`app/code/Vendor/CustomShipping/Model/Carrier/Customshipping.php`}</inlineCode></h3>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php

declare(strict_types=1);

namespace Vendor\\CustomShipping\\Model\\Carrier;

use Magento\\Framework\\App\\Config\\ScopeConfigInterface;
use Magento\\Quote\\Model\\Quote\\Address\\RateRequest;
use Magento\\Quote\\Model\\Quote\\Address\\RateResult\\Method;
use Magento\\Quote\\Model\\Quote\\Address\\RateResult\\MethodFactory;
use Magento\\Quote\\Model\\Quote\\Address\\RateResult\\ErrorFactory;
use Magento\\Shipping\\Model\\Carrier\\AbstractCarrier;
use Magento\\Shipping\\Model\\Carrier\\CarrierInterface;
use Magento\\Shipping\\Model\\Rate\\Result;
use Magento\\Shipping\\Model\\Rate\\ResultFactory;
use Psr\\Log\\LoggerInterface;

class Customshipping extends AbstractCarrier implements CarrierInterface
{
    protected string $_code = 'customshipping';

    protected bool $_isFixed = true;

    private ResultFactory $rateResultFactory;

    private MethodFactory $rateMethodFactory;

    public function __construct(
        ScopeConfigInterface $scopeConfig,
        ErrorFactory $rateErrorFactory,
        LoggerInterface $logger,
        ResultFactory $rateResultFactory,
        MethodFactory $rateMethodFactory,
        array $data = []
    ) {
        parent::__construct($scopeConfig, $rateErrorFactory, $logger, $data);

        $this->rateResultFactory = $rateResultFactory;
        $this->rateMethodFactory = $rateMethodFactory;
    }

    /**
     * Custom Shipping Rates Collector
     *
     * @param RateRequest $request
     * @return \\Magento\\Shipping\\Model\\Rate\\Result|bool
     */
    public function collectRates(RateRequest $request)
    {
        if (!$this->getConfigFlag('active')) {
            return false;
        }

        /** @var Method $method */
        $method = $this->rateMethodFactory->create();

        $method->setCarrier($this->_code);
        $method->setCarrierTitle($this->getConfigData('title'));

        $method->setMethod($this->_code);
        $method->setMethodTitle($this->getConfigData('name'));

        $shippingCost = (float) $this->getConfigData('shipping_cost');
        $method->setPrice($shippingCost);
        $method->setCost($shippingCost);

        /** @var Result $result */
        $result = $this->rateResultFactory->create();
        $result->append($method);

        return $result;
    }

    public function getAllowedMethods(): array
    {
        return [$this->_code => $this->getConfigData('name')];
    }
}
`}</code></pre>
    <h2 {...{
      "id": "step-4-enable-the-module"
    }}>{`Step 4: Enable the module`}</h2>
    <p>{`Run the commands below to register `}<inlineCode parentName="p">{`Vendor_CustomShipping`}</inlineCode>{` module:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento module:enable Vendor_CustomShipping
`}</code></pre>
    <h2 {...{
      "id": "screenshots"
    }}>{`Screenshots`}</h2>
    <p>{`The backend settings for the custom shipping carrier appear as shown below.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "50.31250000000001%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/5530d/checkout-add-custom-carrier-01.webp 320w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/0c8fb/checkout-add-custom-carrier-01.webp 640w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/94b1e/checkout-add-custom-carrier-01.webp 1280w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/0b34d/checkout-add-custom-carrier-01.webp 1920w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/d5269/checkout-add-custom-carrier-01.webp 2560w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/a5b37/checkout-add-custom-carrier-01.webp 3320w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/dd4a7/checkout-add-custom-carrier-01.png 320w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/0f09e/checkout-add-custom-carrier-01.png 640w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/bbbf7/checkout-add-custom-carrier-01.png 1280w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/ac7a9/checkout-add-custom-carrier-01.png 1920w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/c7a69/checkout-add-custom-carrier-01.png 2560w", "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/353b7/checkout-add-custom-carrier-01.png 3320w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/440961b90934efa9da4a324a4e04e7ff/bbbf7/checkout-add-custom-carrier-01.png",
            "alt": "Custom shipping carrier backend settings",
            "title": "Custom shipping carrier backend settings",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`The custom shipping carrier will appear on checkout as shown below.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "60.62500000000001%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/5530d/checkout-add-custom-carrier-02.webp 320w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/0c8fb/checkout-add-custom-carrier-02.webp 640w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/94b1e/checkout-add-custom-carrier-02.webp 1280w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/0b34d/checkout-add-custom-carrier-02.webp 1920w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/fca01/checkout-add-custom-carrier-02.webp 2044w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/dd4a7/checkout-add-custom-carrier-02.png 320w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/0f09e/checkout-add-custom-carrier-02.png 640w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/bbbf7/checkout-add-custom-carrier-02.png 1280w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/ac7a9/checkout-add-custom-carrier-02.png 1920w", "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/d9f65/checkout-add-custom-carrier-02.png 2044w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/76d4db2e265e7b46d88dab672a7b4f24/bbbf7/checkout-add-custom-carrier-02.png",
            "alt": "Custom shipping carrier on checkout",
            "title": "Custom shipping carrier on checkout",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      